import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButton } from '@angular/material/button';

import { TInnerBot } from '@core/interfaces/TBot';
import { TBotItem, TBotItemHistory } from '@core/interfaces/TBotItem';

import { FirstUpperPipe } from '../../../../../shared/pipes/first-upper.pipe';
import { SanitizeStrPipe } from '../../../../../shared/pipes/sanitize-str.pipe';

export type TFieldSet = 'bot' | 'innerBot';

@Component({
    selector: 'app-bot-config-comparison',
    templateUrl: './bot-config-comparison.component.html',
    styleUrls: ['./bot-config-comparison.component.scss'],
    standalone: true,
    imports: [NgClass, MatButton, FirstUpperPipe, SanitizeStrPipe]
})
export class BotConfigComparisonComponent {
  @Input() current: TBotItem | TInnerBot | undefined;
  @Input() version: TBotItemHistory | undefined;
  @Input() fieldSet: TFieldSet = 'bot';
  @Output() revert = new EventEmitter<TBotItemHistory>();
  @Output() delete = new EventEmitter<TBotItemHistory>();
  protected compareFieldsCommon: string[] = [
    'name',
    'instruction',
    'behaviour',
    'description',
    'config',
    'botModel',
  ];
  protected compareFieldsBot: string[] = [
    'display_resources',
    'maximum_publications',
    'maximum_resources',
  ];
  protected compareFieldsInnerBot: string[] = [
    'presence_penalty',
    'temperature',
    'top_p',
    'frequency_penalty',
    'inputDataSource',
    'outputDestination',
  ];

  get compareFields(): string[] {
    return this.compareFieldsCommon.concat(
      this.fieldSet === 'bot' ? this.compareFieldsBot : this.compareFieldsInnerBot
    );
  }

  getValue(object: TBotItem | TInnerBot | TBotItemHistory | undefined, key: string): string {
    if (object) {
      const value = object?.[key as keyof typeof object];
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (typeof value === 'object' && value !== null) {
        // Format inputDataSource
        if (key === 'inputDataSource') {
          return this.formatInputDataSource(value as any);
        }
        // Format outputDestination
        if (key === 'outputDestination') {
          return this.formatOutputDestination(value as any);
        }
        return JSON.stringify(value, null, 2);
      }
      return value?.toString() ?? '';
    }
    return '';
  }

  private formatInputDataSource(data: any): string {
    const parts: string[] = [];

    if (data.includeInitContext !== undefined) {
      parts.push(`Include Init Context: ${data.includeInitContext ? 'Yes' : 'No'}`);
    }

    if (data.includeTools !== undefined) {
      parts.push(`Include Tools: ${data.includeTools ? 'Yes' : 'No'}`);
    }

    if (data.includePreviousBots && data.includePreviousBots.length > 0) {
      parts.push(`Input Bots: ${data.includePreviousBots.length} bot(s)`);
      data.includePreviousBots.forEach((bot: any, index: number) => {
        const botDetails: string[] = [];
        if (bot.includeConversation) botDetails.push('conversation');
        if (bot.includeSearchedSources) botDetails.push('search results');
        parts.push(`  Bot ${index + 1}: ${botDetails.join(', ') || 'none'}`);
      });
    } else {
      parts.push('Input Bots: None');
    }

    return parts.join('\n');
  }

  private formatOutputDestination(data: any): string {
    if (data.type === 'frontend') {
      return 'Frontend (Display to user)';
    } else if (data.type === 'context_only') {
      return 'Context Only (Internal use)';
    }
    return data.type || 'Not specified';
  }

  isDiff(field: string): boolean {
    if (this.version && this.current) {
      return this.getValue(this.version, field) !== this.getValue(this.current, field);
    }
    return false;
  }

  onRevert() {
    this.revert.emit(this.version);
  }

  onDelete() {
    this.delete.emit(this.version);
  }
}
